import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FolderLock,
  Star,
  Clock,
  Users,
  Network,
  Folder,
  Tag,
  Layers,
  AlertTriangle,
  Trash2,
  Plus,
  Search,
  LayoutGrid,
  List as ListIcon,
  RefreshCw,
  MoreVertical,
  Edit3,
  Download,
  Share2,
  History,
  Lock,
  RotateCcw,
  Check,
  Eye,
  Calendar,
  Sparkles,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileArchive,
  FileCode,
  FileCheck,
  Shield,
  ShieldCheck,
  CheckCircle2,
  X,
  ArrowUpDown,
} from 'lucide-react';
import { MainLayout } from '../../components/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useApplications } from '../../hooks/useApplications';
import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../../crypto/WebCryptoProvider';
import api from '../../api/client';

// Modals
import DocumentUploadModal, {
  getFileFormatInfo,
  formatBytes,
  PREDEFINED_DOC_TYPES,
  PREDEFINED_CATEGORIES,
} from '../../components/documents/DocumentUploadModal';
import DocumentViewModal, { getDocumentExpiry } from '../../components/documents/DocumentViewModal';
import DocumentVersionHistoryModal from '../../components/documents/DocumentVersionHistoryModal';
import DocumentShareModal from '../../components/documents/DocumentShareModal';
import {
  RenameDocumentModal,
  MoveDocumentCategoryModal,
  ManageDocumentTagsModal,
  DeleteDocumentModal,
} from '../../components/documents/DocumentActionModals';

const generateUuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const DEFAULT_DOCUMENT_CATEGORIES = [
  { id: 'cat-personal', name: 'Personal', icon: '👤' },
  { id: 'cat-work', name: 'Work', icon: '💼' },
  { id: 'cat-financial', name: 'Financial', icon: '💳' },
  { id: 'cat-legal', name: 'Legal', icon: '⚖️' },
  { id: 'cat-property', name: 'Property', icon: '🏠' },
  { id: 'cat-insurance', name: 'Insurance', icon: '🛡️' },
  { id: 'cat-vehicle', name: 'Vehicle', icon: '🚗' },
  { id: 'cat-education', name: 'Education', icon: '🎓' },
];

export default function DocumentsApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { vaultKey, user } = useAuth();
  const { hasPermission } = useApplications();

  // URL query params: all | favorites | recent | shared-with-me | shared-by-me | categories | tags | types | expiring | trash
  const searchParams = new URLSearchParams(location.search);
  const activeFilter = searchParams.get('filter') || 'all';
  const urlCategory = searchParams.get('category') || '';
  const docIdParam = searchParams.get('docId');

  const vaultCrypto = useMemo(() => new VaultCrypto(WebCryptoProvider), []);

  // Core State
  const [vaultId, setVaultId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_DOCUMENT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [formatFilter, setFormatFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(urlCategory || 'all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [viewMode, setViewMode] = useState(() => {
    return window.localStorage.getItem('cipher-vault-docs-view') || 'grid';
  });
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  // UI Modals
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [versionDoc, setVersionDoc] = useState(null);
  const [shareDoc, setShareDoc] = useState(null);
  const [renameDoc, setRenameDoc] = useState(null);
  const [moveCatDoc, setMoveCatDoc] = useState(null);
  const [manageTagsDoc, setManageTagsDoc] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { doc, isPermanent }

  // Action Menu Dropdown ID & Responsive State
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    const mq = window.matchMedia('(max-width: 768px)');
    if (mq.addEventListener) {
      mq.addEventListener('change', handleResize);
    } else if (mq.addListener) {
      mq.addListener(handleResize);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      if (mq.removeEventListener) {
        mq.removeEventListener('change', handleResize);
      } else if (mq.removeListener) {
        mq.removeListener(handleResize);
      }
    };
  }, []);

  const activeMenuDoc = useMemo(
    () => documents.find((d) => d.id === activeMenuId) || null,
    [documents, activeMenuId]
  );

  // Toast Notification
  const [toastMessage, setToastMessage] = useState('');
  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3500);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = () => setActiveMenuId(null);
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  // Persist View Mode
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    window.localStorage.setItem('cipher-vault-docs-view', mode);
  };

  // Check Permissions
  const canCreate = hasPermission('documents', 'create');
  const canEdit = hasPermission('documents', 'edit');
  const canDelete = hasPermission('documents', 'delete');
  const canShare = hasPermission('documents', 'share');

  // Load Documents from Zero-Knowledge Vault
  const loadDocuments = useCallback(async () => {
    if (!vaultKey) return;
    setLoading(true);
    setLoadError(null);

    try {
      // 1. Get Primary Vault
      const vaultsRes = await api.get('/vaults');
      const primaryVault = vaultsRes.data?.[0];
      if (!primaryVault) {
        setLoadError('No vault initialized. Please create or unlock your vault.');
        setLoading(false);
        return;
      }
      setVaultId(primaryVault.id);

      // 2. Fetch Vault Items
      const itemsRes = await api.get(`/vaults/${primaryVault.id}/items`);
      const allItems = itemsRes.data || [];
      const docItems = allItems.filter((i) => i.item_type === 'DOCUMENT');

      // 3. Decrypt Items
      const decrypted = await Promise.all(
        docItems.map(async (item) => {
          try {
            const raw = await vaultCrypto.decryptItem(item.encrypted_data, vaultKey, item.id);
            const parsed = JSON.parse(raw);
            return {
              ...parsed,
              id: item.id,
              vault_id: item.vault_id,
              status: item.status || parsed.status || 'ACTIVE',
              is_favorite: Boolean(item.is_favorite ?? parsed.is_favorite),
              isFavorite: Boolean(item.is_favorite ?? parsed.is_favorite),
              created_at: item.created_at || parsed.created_at,
              updated_at: item.updated_at || parsed.updated_at,
            };
          } catch (err) {
            return {
              id: item.id,
              vault_id: item.vault_id,
              title: 'Encrypted Document (Decryption Error)',
              status: item.status || 'ACTIVE',
              is_favorite: Boolean(item.is_favorite),
              decryptionFailed: true,
            };
          }
        })
      );

      setDocuments(decrypted);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setLoadError('Failed to load zero-knowledge documents. Verify connection.');
    } finally {
      setLoading(false);
    }
  }, [vaultKey, vaultCrypto, user]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Handle URL Category
  useEffect(() => {
    if (urlCategory) setCategoryFilter(urlCategory);
  }, [urlCategory]);

  // Handle URL docIdParam
  useEffect(() => {
    if (docIdParam && documents.length > 0) {
      const match = documents.find((d) => d.id === docIdParam);
      if (match) setViewingDoc(match);
    }
  }, [docIdParam, documents]);

  // Expiry Statistics
  const expiryStats = useMemo(() => {
    let expiredCount = 0;
    let expiringSoonCount = 0;

    documents.forEach((d) => {
      if (d.status === 'TRASHED') return;
      const f = d.fields || d;
      if (f.expiryDate) {
        const info = getDocumentExpiry(f.expiryDate);
        if (info.status === 'expired') expiredCount++;
        else if (info.status === 'expiring_soon') expiringSoonCount++;
      }
    });

    return { expiredCount, expiringSoonCount };
  }, [documents]);

  // Filtered Documents
  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      const f = doc.fields || doc;

      // Trash Filter
      if (activeFilter === 'trash') {
        return doc.status === 'TRASHED';
      }
      if (doc.status === 'TRASHED') return false;

      // Sidebar route filters
      if (activeFilter === 'favorites' && !(doc.is_favorite || doc.isFavorite)) return false;
      if (activeFilter === 'recent') {
        // Last 30 days
        const itemDate = new Date(doc.updated_at || doc.created_at || 0).getTime();
        const thirtyDaysAgo = Date.now() - 86400000 * 30;
        if (itemDate < thirtyDaysAgo) return false;
      }
      if (activeFilter === 'shared-with-me') {
        const userEmail = (user?.email || '').toLowerCase();
        const isShared = Array.isArray(doc.shares) && doc.shares.some((s) => s.email?.toLowerCase() === userEmail);
        if (!isShared) return false;
      }
      if (activeFilter === 'shared-by-me') {
        if (!Array.isArray(doc.shares) || doc.shares.length === 0) return false;
      }
      if (activeFilter === 'expiring') {
        const exp = getDocumentExpiry(f.expiryDate);
        if (exp.status !== 'expiring_soon' && exp.status !== 'expired') return false;
      }

      // Dropdown Type filter
      if (typeFilter !== 'all') {
        if (f.documentType !== typeFilter) return false;
      }

      // Dropdown Format filter
      if (formatFilter !== 'all') {
        const info = getFileFormatInfo(doc.fileName || doc.title);
        if (formatFilter === 'pdf' && info.format !== 'PDF') return false;
        if (formatFilter === 'word' && !['DOC', 'DOCX'].includes(info.format)) return false;
        if (formatFilter === 'excel' && !['XLS', 'XLSX', 'CSV'].includes(info.format)) return false;
        if (formatFilter === 'images' && !['JPG', 'JPEG', 'PNG', 'WEBP', 'SVG'].includes(info.format)) return false;
        if (formatFilter === 'text' && !['TXT', 'MD'].includes(info.format)) return false;
        if (formatFilter === 'archives' && !['ZIP', 'RAR', 'TAR', 'GZ'].includes(info.format)) return false;
      }

      // Dropdown Category filter
      if (categoryFilter !== 'all') {
        if ((doc.category || '').toLowerCase() !== categoryFilter.toLowerCase()) return false;
      }

      // Dropdown Expiry filter
      if (expiryFilter !== 'all') {
        const exp = getDocumentExpiry(f.expiryDate);
        if (expiryFilter === 'expired' && exp.status !== 'expired') return false;
        if (expiryFilter === 'expiring_soon' && exp.status !== 'expiring_soon') return false;
        if (expiryFilter === 'valid' && exp.status !== 'valid') return false;
      }

      // Search query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const titleMatch = (doc.title || '').toLowerCase().includes(q);
        const fileNameMatch = (doc.fileName || '').toLowerCase().includes(q);
        const descMatch = (f.description || '').toLowerCase().includes(q);
        const ownerMatch = (doc.owner || '').toLowerCase().includes(q);
        const catMatch = (doc.category || '').toLowerCase().includes(q);
        const tagsMatch = Array.isArray(doc.tags) && doc.tags.some((t) => t.toLowerCase().includes(q));

        if (!titleMatch && !fileNameMatch && !descMatch && !ownerMatch && !catMatch && !tagsMatch) {
          return false;
        }
      }

      return true;
    });
  }, [documents, activeFilter, typeFilter, formatFilter, categoryFilter, expiryFilter, searchQuery, user]);

  // Sorted Documents
  const sortedDocuments = useMemo(() => {
    const list = [...filteredDocuments];
    return list.sort((a, b) => {
      const fa = a.fields || a;
      const fb = b.fields || b;

      if (sortBy === 'title_asc') {
        return (a.title || '').localeCompare(b.title || '');
      }
      if (sortBy === 'title_desc') {
        return (b.title || '').localeCompare(a.title || '');
      }
      if (sortBy === 'size_desc') {
        return (b.fileSize || 0) - (a.fileSize || 0);
      }
      if (sortBy === 'expiry_soonest') {
        const expA = fa.expiryDate ? new Date(fa.expiryDate).getTime() : Infinity;
        const expB = fb.expiryDate ? new Date(fb.expiryDate).getTime() : Infinity;
        return expA - expB;
      }
      if (sortBy === 'created_desc') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      // default: updated_desc
      return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    });
  }, [filteredDocuments, sortBy]);

  // ─────────────────────────────────────────────
  // CRUD Actions
  // ─────────────────────────────────────────────

  // Save (Create or Update)
  const handleSaveDocument = async (payload) => {
    if (!vaultId || !vaultKey) {
      showToast('Vault credentials not available. Please unlock your vault.');
      return;
    }

    const isUpdating = Boolean(editingDoc?.id);
    const docId = isUpdating ? editingDoc.id : generateUuid();
    const now = new Date().toISOString();

    const previousVersions = editingDoc?.versions || [];
    const newVersionNumber = previousVersions.length + 1;

    const currentVersionRecord = {
      version: newVersionNumber,
      savedAt: now,
      modifier: user?.name || user?.email || 'You',
      fileSize: payload.fileSize || editingDoc?.fileSize || 102400,
      summary: isUpdating ? 'Updated document details and metadata' : 'Document uploaded and encrypted',
    };

    const fullDocObj = {
      ...editingDoc,
      ...payload,
      id: docId,
      vault_id: vaultId,
      is_favorite: Boolean(payload.is_favorite ?? editingDoc?.is_favorite),
      isFavorite: Boolean(payload.is_favorite ?? editingDoc?.is_favorite),
      status: editingDoc?.status || 'ACTIVE',
      created_at: editingDoc?.created_at || now,
      updated_at: now,
      versions: [currentVersionRecord, ...previousVersions],
    };

    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(fullDocObj),
        vaultKey,
        docId
      );

      let saved = false;
      if (isUpdating) {
        try {
          await api.put(`/vaults/${vaultId}/items/${docId}`, {
            id: docId,
            itemType: 'DOCUMENT',
            encryptedData,
            isFavorite: fullDocObj.is_favorite,
            status: fullDocObj.status,
          });
          saved = true;
        } catch (putErr) {
          if (putErr.response?.status !== 404 && putErr.response?.status !== 400) throw putErr;
        }
      }

      if (!saved) {
        await api.post(`/vaults/${vaultId}/items`, {
          id: docId,
          itemType: 'DOCUMENT',
          encryptedData,
          isFavorite: fullDocObj.is_favorite,
          status: fullDocObj.status,
        });
      }

      if (isUpdating) {
        setDocuments((prev) => prev.map((d) => (d.id === docId ? fullDocObj : d)));
        if (viewingDoc?.id === docId) setViewingDoc(fullDocObj);
        showToast('Document updated successfully');
      } else {
        setDocuments((prev) => [fullDocObj, ...prev]);
        showToast('New document encrypted & stored');
      }

      setIsUploadOpen(false);
      setEditingDoc(null);
    } catch (err) {
      console.error('Failed to save document:', err);
      showToast(isUpdating ? 'Failed to update document' : 'Failed to upload document');
    }
  };

  // Toggle Favorite
  const handleToggleFavorite = async (target) => {
    if (!vaultId || !vaultKey) return;
    const currentFav = Boolean(target.is_favorite || target.isFavorite);
    const newFav = !currentFav;
    let docId = target.id;
    const isTargetUuid = typeof docId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(docId);
    if (!isTargetUuid) {
      docId = generateUuid();
    }
    const updated = {
      ...target,
      id: docId,
      is_favorite: newFav,
      isFavorite: newFav,
      updated_at: new Date().toISOString(),
    };

    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        docId
      );

      let saved = false;
      if (isTargetUuid) {
        try {
          await api.put(`/vaults/${vaultId}/items/${docId}`, {
            id: docId,
            itemType: 'DOCUMENT',
            encryptedData,
            isFavorite: newFav,
            status: target.status || 'ACTIVE',
          });
          saved = true;
        } catch (putErr) {
          if (putErr.response?.status !== 404 && putErr.response?.status !== 400) throw putErr;
        }
      }

      if (!saved) {
        await api.post(`/vaults/${vaultId}/items`, {
          id: docId,
          itemType: 'DOCUMENT',
          encryptedData,
          isFavorite: newFav,
          status: target.status || 'ACTIVE',
        });
      }

      setDocuments((prev) => prev.map((d) => (d.id === target.id || d.id === docId ? updated : d)));
      if (viewingDoc?.id === target.id || viewingDoc?.id === docId) setViewingDoc(updated);
      showToast(newFav ? 'Added to Favorites' : 'Removed from Favorites');
    } catch (err) {
      console.error('Favorite update error:', err);
      showToast('Failed to update favorite status');
    }
  };

  // Rename Document
  const handleRenameDocument = async (target, newTitle) => {
    if (!vaultId || !vaultKey) return;
    const updated = {
      ...target,
      title: newTitle,
      updated_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(JSON.stringify(updated), vaultKey, target.id);
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'DOCUMENT',
        encryptedData,
        isFavorite: target.is_favorite,
        status: target.status,
      });
      setDocuments((prev) => prev.map((d) => (d.id === target.id ? updated : d)));
      if (viewingDoc?.id === target.id) setViewingDoc(updated);
      showToast(`Document renamed to "${newTitle}"`);
    } catch (err) {
      showToast('Failed to rename document');
    }
  };

  // Move Category
  const handleMoveCategory = async (target, newCategory) => {
    if (!vaultId || !vaultKey) return;
    const updated = {
      ...target,
      category: newCategory,
      updated_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(JSON.stringify(updated), vaultKey, target.id);
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'DOCUMENT',
        encryptedData,
        isFavorite: target.is_favorite,
        status: target.status,
      });
      setDocuments((prev) => prev.map((d) => (d.id === target.id ? updated : d)));
      if (viewingDoc?.id === target.id) setViewingDoc(updated);
      showToast(`Moved to category "${newCategory}"`);
    } catch (err) {
      showToast('Failed to move category');
    }
  };

  // Save Tags
  const handleSaveTags = async (target, newTags) => {
    if (!vaultId || !vaultKey) return;
    const updated = {
      ...target,
      tags: newTags,
      updated_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(JSON.stringify(updated), vaultKey, target.id);
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'DOCUMENT',
        encryptedData,
        isFavorite: target.is_favorite,
        status: target.status,
      });
      setDocuments((prev) => prev.map((d) => (d.id === target.id ? updated : d)));
      if (viewingDoc?.id === target.id) setViewingDoc(updated);
      showToast('Tags updated successfully');
    } catch (err) {
      showToast('Failed to update tags');
    }
  };

  // Save Shares
  const handleSaveShares = async (target, newShares) => {
    if (!vaultId || !vaultKey) return;
    const updated = {
      ...target,
      shares: newShares,
      updated_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(JSON.stringify(updated), vaultKey, target.id);
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'DOCUMENT',
        encryptedData,
        isFavorite: target.is_favorite,
        status: target.status,
      });
      setDocuments((prev) => prev.map((d) => (d.id === target.id ? updated : d)));
      if (viewingDoc?.id === target.id) setViewingDoc(updated);
      showToast('Document sharing updated');
    } catch (err) {
      showToast('Failed to update sharing');
    }
  };

  // Restore Version
  const handleRestoreVersion = async (versionObj) => {
    if (!vaultId || !vaultKey || !versionDoc) return;
    const now = new Date().toISOString();
    const updated = {
      ...versionDoc,
      fileSize: versionObj.fileSize || versionDoc.fileSize,
      updated_at: now,
      versions: [
        {
          version: (versionDoc.versions?.length || 1) + 1,
          savedAt: now,
          modifier: user?.name || 'You',
          fileSize: versionObj.fileSize || versionDoc.fileSize,
          summary: `Restored back to Version ${versionObj.version}`,
        },
        ...(versionDoc.versions || []),
      ],
    };

    try {
      const encryptedData = await vaultCrypto.encryptItem(JSON.stringify(updated), vaultKey, versionDoc.id);
      await api.put(`/vaults/${vaultId}/items/${versionDoc.id}`, {
        id: versionDoc.id,
        itemType: 'DOCUMENT',
        encryptedData,
        isFavorite: versionDoc.is_favorite,
        status: versionDoc.status,
      });
      setDocuments((prev) => prev.map((d) => (d.id === versionDoc.id ? updated : d)));
      if (viewingDoc?.id === versionDoc.id) setViewingDoc(updated);
      showToast(`Restored to Version ${versionObj.version}`);
    } catch (err) {
      showToast('Failed to restore version');
    }
  };

  // Move to Trash (Soft Delete)
  const handleMoveToTrash = async (target) => {
    if (!canDelete || !vaultId || !vaultKey) return;
    const updated = {
      ...target,
      status: 'TRASHED',
      trashed_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(JSON.stringify(updated), vaultKey, target.id);
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'DOCUMENT',
        encryptedData,
        isFavorite: target.is_favorite,
        status: 'TRASHED',
      });
      setDocuments((prev) => prev.map((d) => (d.id === target.id ? updated : d)));
      if (viewingDoc?.id === target.id) setViewingDoc(null);
      showToast('Document moved to Trash');
    } catch (err) {
      showToast('Failed to move document to Trash');
    }
  };

  // Restore from Trash
  const handleRestoreFromTrash = async (target) => {
    if (!vaultId || !vaultKey) return;
    const updated = {
      ...target,
      status: 'ACTIVE',
      trashed_at: null,
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(JSON.stringify(updated), vaultKey, target.id);
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'DOCUMENT',
        encryptedData,
        isFavorite: target.is_favorite,
        status: 'ACTIVE',
      });
      setDocuments((prev) => prev.map((d) => (d.id === target.id ? updated : d)));
      showToast('Document restored from Trash');
    } catch (err) {
      showToast('Failed to restore document');
    }
  };

  // Delete Permanently
  const handlePermanentDelete = async (target) => {
    if (!canDelete || !vaultId) return;
    try {
      await api.delete(`/vaults/${vaultId}/items/${target.id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== target.id));
      if (viewingDoc?.id === target.id) setViewingDoc(null);
      showToast('Document permanently erased');
    } catch (err) {
      showToast('Failed to erase document');
    }
  };

  // Compute Total Storage Size
  const totalStorageBytes = useMemo(() => {
    return documents.reduce((acc, d) => acc + (d.status !== 'TRASHED' ? (d.fileSize || 0) : 0), 0);
  }, [documents]);

  // Dynamic View Titles
  const getFilterTitle = () => {
    switch (activeFilter) {
      case 'favorites':
        return 'Favorite Documents';
      case 'recent':
        return 'Recent Documents';
      case 'shared-with-me':
        return 'Shared With Me';
      case 'shared-by-me':
        return 'Shared By Me';
      case 'categories':
        return 'Document Categories';
      case 'tags':
        return 'Document Tags';
      case 'types':
        return 'Document Types';
      case 'expiring':
        return 'Expiring Documents';
      case 'trash':
        return 'Trash';
      default:
        return 'All Documents';
    }
  };

  const renderDocActionMenu = (item, isMobileModal = false) => {
    const f = item.fields || item;
    const formatInfo = getFileFormatInfo(item.fileName || item.title);
    const isFav = Boolean(item.is_favorite || item.isFavorite);

    return (
      <>
        <div className="doc-menu-header">
          <div className="doc-menu-title" title={item.title}>{item.title}</div>
          <div className="doc-menu-sub">
            {formatInfo.format} • {item.category || 'Personal'}
          </div>
        </div>
        <div className="doc-menu-header-divider" />

        {activeFilter === 'trash' ? (
          <>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                handleRestoreFromTrash(item);
              }}
            >
              <RotateCcw size={15} />
              <span>Restore Document</span>
            </button>
            <button
              type="button"
              className="dropdown-item text-danger"
              onClick={() => {
                setActiveMenuId(null);
                setDeleteTarget({ doc: item, isPermanent: true });
              }}
            >
              <Trash2 size={15} />
              <span>Erase Forever</span>
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                setViewingDoc(item);
              }}
            >
              <Eye size={15} />
              <span>Open / View Preview</span>
            </button>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                setViewingDoc(item);
              }}
            >
              <Download size={15} />
              <span>Download Decrypted File</span>
            </button>
            {canEdit && (
              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setActiveMenuId(null);
                  setRenameDoc(item);
                }}
              >
                <Edit3 size={15} />
                <span>Rename Document</span>
              </button>
            )}
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                handleToggleFavorite(item);
              }}
            >
              <Star size={15} fill={isFav ? 'currentColor' : 'none'} />
              <span>{isFav ? 'Remove from Favorites' : 'Add to Favorites'}</span>
            </button>
            {canEdit && (
              <>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => {
                    setActiveMenuId(null);
                    setMoveCatDoc(item);
                  }}
                >
                  <Folder size={15} />
                  <span>Move Category</span>
                </button>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => {
                    setActiveMenuId(null);
                    setManageTagsDoc(item);
                  }}
                >
                  <Tag size={15} />
                  <span>Manage Tags</span>
                </button>
              </>
            )}
            {canShare && (
              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setActiveMenuId(null);
                  setShareDoc(item);
                }}
              >
                <Share2 size={15} />
                <span>Share & Permissions</span>
              </button>
            )}
            <button
              type="button"
              className="dropdown-item"
              onClick={() => {
                setActiveMenuId(null);
                setVersionDoc(item);
              }}
            >
              <History size={15} />
              <span>Version History</span>
            </button>
            <div className="dropdown-divider" />
            {canDelete && (
              <button
                type="button"
                className="dropdown-item text-danger"
                onClick={() => {
                  setActiveMenuId(null);
                  setDeleteTarget({ doc: item, isPermanent: false });
                }}
              >
                <Trash2 size={15} />
                <span>Move to Trash</span>
              </button>
            )}
          </>
        )}

        {isMobileModal && (
          <div className="doc-menu-cancel-row">
            <button
              type="button"
              className="doc-menu-cancel-btn"
              onClick={() => setActiveMenuId(null)}
            >
              Cancel
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <MainLayout>
      {/* Toast */}
      {toastMessage && (
        <div className="notes-toast animate-slide-up">
          <CheckCircle2 size={16} className="text-success" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Top Header Banner - Full Bleed Edge-to-Edge with Multicolor Gradient */}
      <div className="notes-module-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="notes-header-left">
            <div
              className="notes-app-icon-badge notes-hero-icon-badge"
              style={{ background: 'rgba(255, 255, 255, 0.2)', color: '#ffffff' }}
            >
              <FolderLock size={26} />
            </div>
            <div>
              <div className="notes-header-title-row">
                <h1 className="notes-page-title notes-hero-title">{getFilterTitle()}</h1>
              </div>
              <p className="notes-page-subtitle notes-hero-subtitle">
                {activeFilter === 'trash'
                  ? `${sortedDocuments.length} trashed document(s)`
                  : `${sortedDocuments.length} document(s) • ${formatBytes(totalStorageBytes)} encrypted storage`}
              </p>
            </div>
          </div>

          <div className="notes-header-actions">
            {activeFilter !== 'trash' && canCreate && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary doc-header-btn notes-hero-btn-secondary doc-auto-detect-btn"
                  onClick={() => {
                    setEditingDoc(null);
                    setIsUploadOpen(true);
                  }}
                  title="Analyze document with OCR & Auto-Categorization"
                >
                  <Sparkles size={15} color="#fde047" />
                  <span className="doc-btn-text-full">Auto-Detect & Categorize</span>
                  <span className="doc-btn-text-short">Auto-Detect</span>
                </button>
                <button
                  type="button"
                  className="btn btn-primary doc-header-btn notes-hero-btn-primary doc-upload-btn"
                  onClick={() => {
                    setEditingDoc(null);
                    setIsUploadOpen(true);
                  }}
                  title="Upload Document"
                >
                  <Plus size={15} />
                  <span className="doc-btn-text-full">Upload Document</span>
                  <span className="doc-btn-text-short">Upload</span>
                </button>
              </>
            )}

            <button
              type="button"
              className="btn-icon doc-refresh-btn notes-hero-btn-secondary"
              onClick={loadDocuments}
              title="Refresh and sync documents"
              aria-label="Refresh and sync documents"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      <div className="notes-app-container animate-fade-in">

        {/* Expiry Alert Banner */}
        {activeFilter !== 'trash' && (expiryStats.expiredCount > 0 || expiryStats.expiringSoonCount > 0) && (
          <div className="identity-expiry-alert-banner doc-expiry-banner">
            <div className="identity-expiry-alert-left doc-expiry-left">
              <AlertTriangle size={18} className="text-warning flex-shrink-0" />
              <div className="doc-expiry-text">
                <strong className="doc-expiry-title">
                  <span className="doc-text-desktop">Document Expiry & Renewal Reminders</span>
                  <span className="doc-text-mobile">Expiry Reminders</span>
                </strong>
                <p className="doc-expiry-desc">
                  <span className="doc-text-desktop">
                    {expiryStats.expiredCount > 0 && `${expiryStats.expiredCount} document(s) expired. `}
                    {expiryStats.expiringSoonCount > 0 &&
                      `${expiryStats.expiringSoonCount} document(s) expiring within 30 days. `}
                    Please review renewal or replacement policies.
                  </span>
                  <span className="doc-text-mobile">
                    {expiryStats.expiredCount > 0 && `${expiryStats.expiredCount} expired`}
                    {expiryStats.expiredCount > 0 && expiryStats.expiringSoonCount > 0 && ' · '}
                    {expiryStats.expiringSoonCount > 0 && `${expiryStats.expiringSoonCount} expiring ≤30d`}
                  </span>
                </p>
              </div>
            </div>
            <div className="identity-expiry-alert-actions doc-expiry-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm doc-expiry-btn"
                onClick={() => setExpiryFilter(expiryStats.expiredCount > 0 ? 'expired' : 'expiring_soon')}
              >
                <span className="doc-text-desktop">View Expiring</span>
                <span className="doc-text-mobile">View</span>
              </button>
            </div>
          </div>
        )}

        {/* Main Workspace Sub-views */}
        {activeFilter === 'categories' ? (
          /* Categories View */
          <div className="identity-categories-view">
            <div className="identity-categories-header">
              <h3>Document Categories</h3>
              <p className="text-secondary text-sm">
                Browse encrypted files grouped by business and personal classification domains.
              </p>
            </div>

            <div className="identity-categories-grid">
              {categories.map((cat) => {
                const count = documents.filter(
                  (d) => d.status !== 'TRASHED' && (d.category || '').toLowerCase() === cat.name.toLowerCase()
                ).length;
                return (
                  <div
                    key={cat.id || cat.name}
                    className="identity-category-card glass-panel"
                    onClick={() => {
                      setCategoryFilter(cat.name);
                      navigate(`/apps/documents?category=${encodeURIComponent(cat.name)}`);
                    }}
                  >
                    <div className="identity-cat-icon">{cat.icon || '📁'}</div>
                    <div className="identity-cat-info">
                      <h4>{cat.name}</h4>
                      <span className="text-secondary text-sm">{count} Documents</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : activeFilter === 'types' ? (
          /* Document Types View */
          <div className="identity-categories-view">
            <div className="identity-categories-header">
              <h3>Document Types & Classification</h3>
              <p className="text-secondary text-sm">
                Explore documents categorized by legal agreements, certificates, financial records, and official files.
              </p>
            </div>

            <div className="identity-categories-grid">
              {PREDEFINED_DOC_TYPES.map((t) => {
                const count = documents.filter(
                  (d) => d.status !== 'TRASHED' && (d.documentType || d.fields?.documentType) === t.id
                ).length;
                const IconComp = t.icon;
                return (
                  <div
                    key={t.id}
                    className="identity-category-card glass-panel"
                    onClick={() => {
                      setTypeFilter(t.id);
                      navigate('/apps/documents');
                    }}
                  >
                    <div className="identity-cat-icon" style={{ color: t.color }}>
                      <IconComp size={24} />
                    </div>
                    <div className="identity-cat-info">
                      <h4>{t.label}</h4>
                      <span className="text-secondary text-sm">{count} Documents</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : activeFilter === 'tags' ? (
          /* Tags Cloud View */
          <div className="identity-categories-view">
            <div className="identity-categories-header">
              <h3>Document Tags</h3>
              <p className="text-secondary text-sm">
                Filter documents by assigned cryptographic security tags and labels.
              </p>
            </div>

            <div className="doc-tags-cloud-box glass-panel">
              {(() => {
                const tagMap = {};
                documents.forEach((d) => {
                  if (d.status === 'TRASHED' || !Array.isArray(d.tags)) return;
                  d.tags.forEach((t) => {
                    tagMap[t] = (tagMap[t] || 0) + 1;
                  });
                });
                const tagKeys = Object.keys(tagMap);
                const BASELINE_TAGS = ['important', 'legal', 'tax', 'identity', 'contract', 'finance', 'personal', 'work'];
                const displayTags = tagKeys.length > 0 ? tagKeys : BASELINE_TAGS;
                return displayTags.map((tagKey) => (
                  <button
                    key={tagKey}
                    type="button"
                    className="doc-cloud-tag-btn"
                    onClick={() => {
                      setSearchQuery(tagKey);
                      navigate('/apps/documents');
                    }}
                  >
                    <span className="cloud-tag-name">#{tagKey}</span>
                    <span className="cloud-tag-count">{tagMap[tagKey] || 0}</span>
                  </button>
                ));
              })()}
            </div>
          </div>
        ) : (
          /* Standard Documents Dashboard (All, Favorites, Recent, Expiring, Trash) */
          <>
            {/* Desktop Toolbar */}
            <div className="notes-toolbar doc-toolbar documents-desktop-toolbar">
              <div className="notes-toolbar-left doc-toolbar-left">
                {/* Search */}
                <div className="notes-search-box doc-search-box">
                  <Search size={15} className="search-icon" />
                  <input
                    type="text"
                    className="notes-search-input"
                    placeholder="Search documents, tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="notes-search-clear"
                      onClick={() => setSearchQuery('')}
                    >
                      &times;
                    </button>
                  )}
                </div>

                {/* Type Filter */}
                <select
                  className="notes-select doc-select"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">All Document Types</option>
                  {PREDEFINED_DOC_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>

                {/* Format Filter */}
                <select
                  className="notes-select doc-select"
                  value={formatFilter}
                  onChange={(e) => setFormatFilter(e.target.value)}
                >
                  <option value="all">All Formats</option>
                  <option value="pdf">PDF Documents</option>
                  <option value="word">Word (DOC/DOCX)</option>
                  <option value="excel">Excel (XLS/XLSX/CSV)</option>
                  <option value="images">Images (JPG/PNG/WEBP)</option>
                  <option value="text">Text (TXT/MD)</option>
                  <option value="archives">Archives (ZIP/RAR)</option>
                </select>

                {/* Category Filter */}
                <select
                  className="notes-select doc-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {PREDEFINED_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>

                {/* Expiry Filter */}
                <select
                  className="notes-select doc-select"
                  value={expiryFilter}
                  onChange={(e) => setExpiryFilter(e.target.value)}
                >
                  <option value="all">All Expiry States</option>
                  <option value="valid">Valid Documents</option>
                  <option value="expiring_soon">Expiring Soon (≤30d)</option>
                  <option value="expired">Expired Documents</option>
                </select>
              </div>

              <div className="notes-toolbar-right doc-toolbar-right">
                {/* Sort dropdown */}
                <select
                  className="notes-select doc-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="updated_desc">Recently Modified</option>
                  <option value="title_asc">Name (A - Z)</option>
                  <option value="title_desc">Name (Z - A)</option>
                  <option value="size_desc">File Size (Largest)</option>
                  <option value="expiry_soonest">Expiry: Soonest First</option>
                  <option value="created_desc">Upload Date</option>
                </select>

                {/* Grid / List View Toggle */}
                <div className="notes-view-mode-switch">
                  <button
                    type="button"
                    className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => toggleViewMode('grid')}
                    title="Grid View"
                  >
                    <LayoutGrid size={16} />
                  </button>
                  <button
                    type="button"
                    className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => toggleViewMode('list')}
                    title="List View"
                  >
                    <ListIcon size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Single-Row Filter Bar */}
            <div className="documents-mobile-filter-bar">
              {/* Search Toggle */}
              <button
                type="button"
                className={`documents-mobile-icon-btn ${searchQuery || isMobileSearchOpen ? 'is-active' : ''}`}
                onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
                title="Search documents"
                aria-label="Search documents"
              >
                <Search size={17} />
                {searchQuery && <span className="documents-filter-dot" />}
              </button>

              {/* Document Type Filter */}
              <div
                className={`documents-mobile-icon-btn ${typeFilter !== 'all' ? 'is-active' : ''}`}
                title="Filter by document type"
              >
                <Layers size={17} />
                {typeFilter !== 'all' && <span className="documents-filter-dot" />}
                <select
                  className="documents-filter-hidden-select"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  aria-label="Filter by document type"
                >
                  <option value="all">All Document Types</option>
                  {PREDEFINED_DOC_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Format Filter */}
              <div
                className={`documents-mobile-icon-btn ${formatFilter !== 'all' ? 'is-active' : ''}`}
                title="Filter by format"
              >
                <FileCheck size={17} />
                {formatFilter !== 'all' && <span className="documents-filter-dot" />}
                <select
                  className="documents-filter-hidden-select"
                  value={formatFilter}
                  onChange={(e) => setFormatFilter(e.target.value)}
                  aria-label="Filter by format"
                >
                  <option value="all">All Formats</option>
                  <option value="pdf">PDF Documents</option>
                  <option value="word">Word (DOC/DOCX)</option>
                  <option value="excel">Excel (XLS/XLSX/CSV)</option>
                  <option value="images">Images (JPG/PNG/WEBP)</option>
                  <option value="text">Text (TXT/MD)</option>
                  <option value="archives">Archives (ZIP/RAR)</option>
                </select>
              </div>

              {/* Category Filter */}
              <div
                className={`documents-mobile-icon-btn ${categoryFilter !== 'all' ? 'is-active' : ''}`}
                title="Filter by category"
              >
                <Folder size={17} />
                {categoryFilter !== 'all' && <span className="documents-filter-dot" />}
                <select
                  className="documents-filter-hidden-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  aria-label="Filter by category"
                >
                  <option value="all">All Categories</option>
                  {PREDEFINED_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* Expiry Filter */}
              <div
                className={`documents-mobile-icon-btn ${expiryFilter !== 'all' ? 'is-active' : ''}`}
                title="Filter by expiry"
              >
                <Clock size={17} />
                {expiryFilter !== 'all' && <span className="documents-filter-dot" />}
                <select
                  className="documents-filter-hidden-select"
                  value={expiryFilter}
                  onChange={(e) => setExpiryFilter(e.target.value)}
                  aria-label="Filter by expiry"
                >
                  <option value="all">All Expiry States</option>
                  <option value="valid">Valid Documents</option>
                  <option value="expiring_soon">Expiring Soon (≤30d)</option>
                  <option value="expired">Expired Documents</option>
                </select>
              </div>

              {/* Sort Filter */}
              <div
                className={`documents-mobile-icon-btn ${sortBy !== 'updated_desc' ? 'is-active' : ''}`}
                title="Sort documents"
              >
                <ArrowUpDown size={17} />
                {sortBy !== 'updated_desc' && <span className="documents-filter-dot" />}
                <select
                  className="documents-filter-hidden-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort documents"
                >
                  <option value="updated_desc">Recently Modified</option>
                  <option value="title_asc">Name (A - Z)</option>
                  <option value="title_desc">Name (Z - A)</option>
                  <option value="size_desc">File Size (Largest)</option>
                  <option value="expiry_soonest">Expiry: Soonest First</option>
                  <option value="created_desc">Upload Date</option>
                </select>
              </div>

              {/* View Toggle */}
              <div className="documents-mobile-view-toggle">
                <button
                  type="button"
                  className={viewMode === 'grid' ? 'active' : ''}
                  onClick={() => toggleViewMode('grid')}
                  aria-label="Grid view"
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  type="button"
                  className={viewMode === 'list' ? 'active' : ''}
                  onClick={() => toggleViewMode('list')}
                  aria-label="List view"
                >
                  <ListIcon size={16} />
                </button>
              </div>

              {/* Reset if active */}
              {(searchQuery || typeFilter !== 'all' || formatFilter !== 'all' || categoryFilter !== 'all' || expiryFilter !== 'all' || sortBy !== 'updated_desc') && (
                <button
                  type="button"
                  className="documents-mobile-icon-btn documents-mobile-reset-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setTypeFilter('all');
                    setFormatFilter('all');
                    setCategoryFilter('all');
                    setExpiryFilter('all');
                    setSortBy('updated_desc');
                  }}
                  title="Reset all filters"
                  aria-label="Reset all filters"
                >
                  <X size={16} />
                </button>
              )}

              {/* Reload / Refresh Button */}
              <button
                type="button"
                className="documents-mobile-icon-btn documents-mobile-refresh-btn"
                onClick={loadDocuments}
                title="Reload documents"
                aria-label="Reload documents"
              >
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>

            {/* Expandable Mobile Search Bar */}
            {isMobileSearchOpen && (
              <div className="documents-mobile-search-expand animate-slide-down">
                <Search size={15} className="documents-mobile-search-inner-icon" />
                <input
                  type="text"
                  className="documents-mobile-search-input"
                  placeholder="Search documents, tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="documents-mobile-search-clear"
                    onClick={() => setSearchQuery('')}
                  >
                    &times;
                  </button>
                )}
              </div>
            )}

            {/* Content View */}
            {loading ? (
              <div className="notes-loading-state">
                <RefreshCw size={28} className="animate-spin text-accent" />
                <p>Decrypting zero-knowledge document archive...</p>
              </div>
            ) : sortedDocuments.length === 0 ? (
              <div className="notes-empty-state glass-panel">
                <FolderLock size={56} opacity={0.3} />
                <h3>No Documents Found</h3>
                <p>
                  {searchQuery || typeFilter !== 'all' || categoryFilter !== 'all' || expiryFilter !== 'all'
                    ? 'No documents match your filter criteria.'
                    : activeFilter === 'trash'
                    ? 'The trash is currently empty.'
                    : 'Start storing and securing contracts, tax files, receipts, and certificates with zero-knowledge encryption.'}
                </p>
                {activeFilter !== 'trash' && canCreate && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setEditingDoc(null);
                        setIsUploadOpen(true);
                      }}
                    >
                      <Plus size={16} />
                      <span>Upload Document</span>
                    </button>
                  </div>
                )}
              </div>
            ) : viewMode === 'grid' ? (
              /* Grid View */
              <div className="identity-cards-grid doc-cards-grid">
                {sortedDocuments.map((item) => {
                  const f = item.fields || item;
                  const formatInfo = getFileFormatInfo(item.fileName || item.title);
                  const expiryInfo = getDocumentExpiry(f.expiryDate);
                  const docTypeObj = PREDEFINED_DOC_TYPES.find((t) => t.id === (f.documentType || item.documentType)) || PREDEFINED_DOC_TYPES[0];
                  const isLocked = Boolean(item.isLocked);
                  const isFav = Boolean(item.is_favorite || item.isFavorite);

                  return (
                    <div
                      key={item.id}
                      className={`identity-card doc-card glass-panel ${isFav ? 'is-favorite' : ''} ${
                        activeMenuId === item.id ? 'menu-active' : ''
                      }`}
                      onClick={() => setViewingDoc(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Card Header */}
                      <div className="identity-card-header">
                        <div className="doc-card-icon-title-cluster">
                          <div
                            className="doc-format-icon-circle"
                            style={{ background: `${formatInfo.color}18`, color: formatInfo.color }}
                          >
                            <formatInfo.icon size={18} />
                          </div>
                          <div className="doc-card-title-group">
                            <h3 className="doc-card-title" title={item.title}>
                              {item.title}
                            </h3>
                            <div className="doc-card-badges">
                              <span className={`doc-format-badge ${formatInfo.badgeClass}`}>
                                {formatInfo.format}
                              </span>
                              <span className="badge badge-secondary">{docTypeObj.label}</span>
                            </div>
                          </div>
                        </div>

                        {/* Top Action Buttons */}
                        <div
                          className="doc-card-top-actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {activeFilter !== 'trash' && (
                            <button
                              type="button"
                              className={`doc-card-action-btn ${isFav ? 'text-warning is-fav' : ''}`}
                              onClick={() => handleToggleFavorite(item)}
                              title={isFav ? 'Remove Favorite' : 'Mark Favorite'}
                            >
                              <Star size={15} fill={isFav ? 'currentColor' : 'none'} />
                            </button>
                          )}

                          {/* 3-Dot Action Menu */}
                          <div className="notes-card-menu-relative doc-card-menu-anchor">
                            <button
                              type="button"
                              className={`doc-card-action-btn ${activeMenuId === item.id ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.innerWidth <= 768) setIsMobile(true);
                                setActiveMenuId(activeMenuId === item.id ? null : item.id);
                              }}
                              title="More Options"
                            >
                              <MoreVertical size={15} />
                            </button>

                            {!isMobile && activeMenuId === item.id && (
                              <>
                                <div
                                  className="doc-menu-backdrop"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMenuId(null);
                                  }}
                                />
                                <div
                                  className="notes-dropdown-menu animate-scale-in"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {renderDocActionMenu(item, false)}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Card Body: Specs & Expiry */}
                      <div className="doc-card-body">
                        <div className="doc-card-specs-row">
                          <span>{formatBytes(item.fileSize)}</span>
                          <span>•</span>
                          <span>{item.category || 'Personal'}</span>
                          <span>•</span>
                          <span>By {item.owner || 'You'}</span>
                        </div>

                        {/* Expiry Pill */}
                        {expiryInfo.status !== 'none' && (
                          <div className="doc-card-expiry-row">
                            <span
                              className="identity-expiry-status-pill"
                              style={{
                                backgroundColor: `${expiryInfo.color}15`,
                                color: expiryInfo.color,
                                borderColor: `${expiryInfo.color}35`,
                              }}
                            >
                              {expiryInfo.label}
                            </span>
                          </div>
                        )}

                        {/* Tags */}
                        {Array.isArray(item.tags) && item.tags.length > 0 && (
                          <div className="doc-card-tags">
                            {item.tags.slice(0, 3).map((t) => (
                              <span key={t} className="tag-chip">
                                #{t}
                              </span>
                            ))}
                            {item.tags.length > 3 && (
                              <span className="tag-more-pill">+{item.tags.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Card Footer */}
                      <div className="doc-card-footer">
                        <span className="doc-modified-date">
                          Modified {new Date(item.updated_at || item.created_at || Date.now()).toLocaleDateString()}
                        </span>
                        {isLocked && (
                          <span className="doc-locked-pill" title="Security PIN protected">
                            <Lock size={12} /> Protected
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* List View (Table) */
              <div className="identity-table-container doc-table-container glass-panel">
                <table className="identity-table doc-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }} />
                      <th>Document Name</th>
                      <th>Format</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Size</th>
                      <th>Owner</th>
                      <th>Expiry</th>
                      <th>Modified</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDocuments.map((item) => {
                      const f = item.fields || item;
                      const formatInfo = getFileFormatInfo(item.fileName || item.title);
                      const expiryInfo = getDocumentExpiry(f.expiryDate);
                      const docTypeObj = PREDEFINED_DOC_TYPES.find((t) => t.id === (f.documentType || item.documentType)) || PREDEFINED_DOC_TYPES[0];
                      const isFav = Boolean(item.is_favorite || item.isFavorite);

                      return (
                        <tr
                          key={item.id}
                          onClick={() => setViewingDoc(item)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className={`btn-icon btn-sm ${isFav ? 'text-warning' : ''}`}
                              onClick={() => handleToggleFavorite(item)}
                            >
                              <Star size={15} fill={isFav ? 'currentColor' : 'none'} />
                            </button>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <formatInfo.icon size={16} color={formatInfo.color} />
                              <strong>{item.title}</strong>
                            </div>
                          </td>
                          <td>
                            <span className={`doc-format-badge ${formatInfo.badgeClass}`}>
                              {formatInfo.format}
                            </span>
                          </td>
                          <td>{docTypeObj.label}</td>
                          <td>
                            <span className="badge badge-secondary">{item.category || 'Personal'}</span>
                          </td>
                          <td>{formatBytes(item.fileSize)}</td>
                          <td>{item.owner || 'You'}</td>
                          <td>
                            {expiryInfo.status !== 'none' ? (
                              <span
                                className="identity-expiry-status-pill"
                                style={{
                                  backgroundColor: `${expiryInfo.color}15`,
                                  color: expiryInfo.color,
                                  borderColor: `${expiryInfo.color}35`,
                                }}
                              >
                                {expiryInfo.label}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>{new Date(item.updated_at || item.created_at || Date.now()).toLocaleDateString()}</td>
                          <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="btn-icon btn-sm"
                              onClick={() => setViewingDoc(item)}
                              title="View Document"
                            >
                              <Eye size={15} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ───────────────────────────────────────────── */}
        {/* Modals */}
        {/* ───────────────────────────────────────────── */}

        {/* Upload Document Modal */}
        <DocumentUploadModal
          open={isUploadOpen}
          onClose={() => {
            setIsUploadOpen(false);
            setEditingDoc(null);
          }}
          onSave={handleSaveDocument}
          existingDocument={editingDoc}
        />

        {/* Document Viewer Modal */}
        <DocumentViewModal
          open={Boolean(viewingDoc)}
          document={viewingDoc}
          onClose={() => setViewingDoc(null)}
          onEdit={(doc) => {
            setViewingDoc(null);
            setEditingDoc(doc);
            setIsUploadOpen(true);
          }}
          onToggleFavorite={handleToggleFavorite}
          onDelete={(doc) => {
            setViewingDoc(null);
            setDeleteTarget({ doc, isPermanent: false });
          }}
          onShare={(doc) => setShareDoc(doc)}
          onOpenVersions={(doc) => setVersionDoc(doc)}
          hasEditPermission={canEdit}
        />

        {/* Version History Modal */}
        <DocumentVersionHistoryModal
          open={Boolean(versionDoc)}
          document={versionDoc}
          onClose={() => setVersionDoc(null)}
          onRestoreVersion={handleRestoreVersion}
        />

        {/* Share & Permissions Modal */}
        <DocumentShareModal
          open={Boolean(shareDoc)}
          document={shareDoc}
          onClose={() => setShareDoc(null)}
          onSaveShares={handleSaveShares}
        />

        {/* Rename Modal */}
        <RenameDocumentModal
          open={Boolean(renameDoc)}
          document={renameDoc}
          onClose={() => setRenameDoc(null)}
          onRename={handleRenameDocument}
        />

        {/* Move Category Modal */}
        <MoveDocumentCategoryModal
          open={Boolean(moveCatDoc)}
          document={moveCatDoc}
          onClose={() => setMoveCatDoc(null)}
          onMoveCategory={handleMoveCategory}
        />

        {/* Manage Tags Modal */}
        <ManageDocumentTagsModal
          open={Boolean(manageTagsDoc)}
          document={manageTagsDoc}
          onClose={() => setManageTagsDoc(null)}
          onSaveTags={handleSaveTags}
        />

        {/* Delete Confirmation Modal */}
        <DeleteDocumentModal
          open={Boolean(deleteTarget)}
          document={deleteTarget?.doc}
          isPermanent={deleteTarget?.isPermanent}
          onClose={() => setDeleteTarget(null)}
          onConfirm={(doc, isPerm) => {
            if (isPerm) handlePermanentDelete(doc);
            else handleMoveToTrash(doc);
          }}
        />

        {/* Mobile Action Menu Sheet (Rendered via Portal to eliminate clipping & container trap) */}
        {isMobile && activeMenuDoc && createPortal(
          <div className="doc-mobile-menu-portal" role="dialog" aria-modal="true" aria-label="Document Options">
            <div
              className="doc-mobile-backdrop"
              onClick={() => setActiveMenuId(null)}
            />
            <div
              className="notes-dropdown-menu doc-mobile-action-sheet animate-scale-in"
              onClick={(e) => e.stopPropagation()}
            >
              {renderDocActionMenu(activeMenuDoc, true)}
            </div>
          </div>,
          document.body
        )}
      </div>
    </MainLayout>
  );
}
